import { useEffect, useState } from 'react'
import shortid from 'shortid'

import CookieService from 'services/cookie.service'

import { CustomCard } from 'services/apiTypes/customCards.types'
import { CardType, defaultCard } from 'types/deck'

const mergeCastingCost = (element: string) => `{${element}}`

export const convertCustomCardsToCardType = (
  customCards: CustomCard | CustomCard[],
  noId: boolean = false,
  overrideUserId?: number,
  overrideShowUploadedImages?: boolean,
): CardType[] => {
  // prettier-ignore
  const showCustomUploads = overrideShowUploadedImages || (CookieService.get('settings') || {}).show_custom_uploads || false
  const userId = overrideUserId || Number(CookieService.get('tbId') || 0)

  const customCardsAsArray = Array.isArray(customCards) ? [...customCards] : [customCards]

  return customCardsAsArray.map((customCard: CustomCard): CardType => {
    const viewCustomCardUrls = customCard.owner === userId || showCustomUploads

    let baseCard: CardType = { ...defaultCard }
    let front: CardType | null = null
    let back: CardType | null = null

    if (!customCard.hasBack) {
      baseCard = {
        ...defaultCard,
        name: customCard.frontName,
        cmc: customCard.cmc,
        castingCost: customCard.frontManaCost ? customCard.frontManaCost.replace(/[^a-zA-Z0-9]/g, '').split('') : [],
        types: customCard.frontTypes?.split(' ') || [],
        superTypes: customCard.frontSuperTypes?.split(' ') || [],
        subTypes: customCard.frontSubTypes?.split(' ') || [],
        power: customCard.frontPower || '',
        toughness: customCard.frontToughness || '',
        loyalty: customCard.frontLoyalty || '',
        text: customCard.frontText || '',
        artist: customCard.frontArtistName || '',
      }
    } else {
      front = {
        ...defaultCard,
        name: customCard.frontName,
        castingCost: customCard.frontManaCost ? customCard.frontManaCost.replace(/[^a-zA-Z0-9]/g, '').split('') : [],
        types: customCard.frontTypes?.split(' ') || [],
        superTypes: customCard.frontSuperTypes?.split(' ') || [],
        subTypes: customCard.frontSubTypes?.split(' ') || [],
        power: customCard.frontPower || '',
        toughness: customCard.frontToughness || '',
        loyalty: customCard.frontLoyalty || '',
        text: customCard.frontText || '',
        artist: customCard.frontArtistName || '',
      }

      back = {
        ...defaultCard,
        name: customCard.backName || '',
        castingCost: customCard.backManaCost ? customCard.backManaCost.replace(/[^a-zA-Z0-9]/g, '').split('') : [],
        types: customCard.backTypes?.split(' ') || [],
        superTypes: customCard.backSuperTypes?.split(' ') || [],
        subTypes: customCard.backSubTypes?.split(' ') || [],
        power: customCard.backPower || '',
        toughness: customCard.backToughness || '',
        loyalty: customCard.backLoyalty || '',
        text: customCard.backText || '',
        artist: customCard.backArtistName || '',
      }
    }

    return {
      ...baseCard,

      id: noId ? '' : shortid.generate(),
      cardId: null,
      deckRelationId: '',
      typeCategory: customCard.frontTypes?.split(' ')[0] || 'Custom Card',
      categories: ['Custom Card'],

      setCode: customCard.setCode || '',
      collectorNumber: customCard.collectorNumber || '',

      customCard: {
        id: customCard.id || null,
        ownerId: customCard.owner || null,
        frontImageUrl: viewCustomCardUrls ? customCard.frontImageUrl : null,
        backImageUrl: viewCustomCardUrls ? customCard.backImageUrl : null,
      },

      // Doing this logic separate from the above logic since the name lives outside of the front / back objects
      name: customCard.hasBack ? `${customCard.frontName || ''} // ${customCard.backName || ''}` : customCard.frontName,
      layout: customCard.hasBack ? 'transform' : 'normal',

      front: customCard.hasBack ? front : null,
      back: customCard.hasBack ? back : null,
    }
  })
}

export const convertCardTypeToCustomCard = (card: CardType): CustomCard => {
  return {
    id: card.customCard?.id || undefined,
    owner: card.customCard?.ownerId || 0,
    cmc: card.cmc,

    frontName: card.front?.name || card.name,
    frontManaCost:
      card.front?.castingCost?.map(mergeCastingCost).join('') || card.castingCost?.map(mergeCastingCost).join('') || '',
    frontText: card.front?.text || card.text || '',
    frontPower: card.front?.power || card.power || '',
    frontToughness: card.front?.toughness || card.toughness || '',
    frontLoyalty: card.front?.loyalty || card.loyalty || '',
    frontTypes: card.front?.types?.join(' ') || card.types?.join(' ') || '',
    frontSubTypes: card.front?.subTypes?.join(' ') || card.subTypes?.join(' ') || '',
    frontSuperTypes: card.front?.superTypes.join(' ') || card.superTypes.join(' ') || '',
    frontImageUrl: card.customCard?.frontImageUrl || '',
    frontArtistName: card.front?.artist || card.artist || '',

    hasBack: !!card.back,

    backName: card.back?.name || '',
    backManaCost: card.back?.castingCost?.join() || '',
    backText: card.back?.text || card.text || '',
    backPower: card.back?.power || card.power || '',
    backToughness: card.back?.toughness || card.toughness || '',
    backLoyalty: card.back?.loyalty || card.loyalty || '',
    backTypes: card.back?.types?.join(' ') || '',
    backSubTypes: card.back?.subTypes?.join(' ') || '',
    backSuperTypes: card.back?.superTypes.join(' ') || '',
    backImageUrl: card.customCard?.backImageUrl || '',
    backArtistName: card.back?.artist || '',

    setCode: card.setCode || '',
    collectorNumber: card.collectorNumber || '',
  }
}

export const useCustomCardAsCardType = (
  customCards: CustomCard | CustomCard[],
  overrideUserId?: number,
  overrideShowUploadedImages?: boolean,
): CardType[] => {
  const [cards, setCards] = useState(convertCustomCardsToCardType(customCards))

  useEffect(() => {
    setCards(convertCustomCardsToCardType(customCards))
  }, [customCards])

  return cards
}

export const useCardTypeAsCustomCard = (card: CardType): CustomCard => {
  const [customCard, setCustomCard] = useState(convertCardTypeToCustomCard(card))

  useEffect(() => {
    setCustomCard(convertCardTypeToCustomCard(card))
  }, [card])

  return customCard
}
